'use es6';

import { createContext, useContext, useState } from 'react';
import * as checked from 'reporting-data/lib/checked';
import { addFilter, moveFilter, removeFilter, removeStagedFilter, updateFilter, updateGroup } from '../filter-utils';
import { jsx as _jsx } from "react/jsx-runtime";
const EditingState = checked.record({
  editingGroupIndex: checked.number().optional(),
  editingFilterIndex: checked.number().optional(),
  editingStagedFilterIndex: checked.number().optional()
}, 'EditingState');
const useEditCallbacks = (sourceMeta, setFiltering, editingState, setEditingState) => {
  let definition;
  switch (sourceMeta.schema) {
    case 'report':
      definition = sourceMeta.report;
      break;
    case 'dataset':
      definition = sourceMeta.dataset;
      break;
    default:
      throw new Error(`Cannot edit unknown schema ${sourceMeta.schema}`);
  }
  const filtering = definition.filtering;
  const {
    groups
  } = filtering;
  const groupCount = groups.count();
  const handleGroupConditionChange = (groupIndex, nextCondition) => {
    const nextFiltering = updateGroup(filtering, groupIndex, group => group.set('condition', nextCondition));
    setFiltering(nextFiltering);
  };
  const handleFilterEdit = (groupIndex, filterIndex) => {
    setEditingState(EditingState({
      editingGroupIndex: groupIndex,
      editingFilterIndex: filterIndex
    }));
  };
  const clearEditingState = () => {
    setEditingState(EditingState({}));
  };
  const handleCancelEdit = () => {
    clearEditingState();
  };
  const handleFilterRemove = (groupIndex, filterIndex) => {
    clearEditingState();
    setFiltering(removeFilter(filtering, groupIndex, filterIndex));
  };
  const handleFilterChange = (draftFilter, draftGroupIndex) => {
    const {
      editingGroupIndex,
      editingFilterIndex
    } = editingState || {};
    clearEditingState();
    const filterType = draftFilter.filter.get('filterType');
    if ((filterType === 'PROPERTY' || filterType === 'DATASET_FIELD') && !draftFilter.filter.get('operation')) {
      const nextFiltering = removeFilter(filtering, editingGroupIndex, editingFilterIndex);
      setFiltering(nextFiltering);
      return;
    }
    const editingGroup = groups.get(editingGroupIndex);
    const toGroupIndex = draftGroupIndex === undefined ? editingGroup.filters.count() === 1 ? editingGroupIndex : groupCount : draftGroupIndex;
    const toGroup = groups.get(toGroupIndex);
    const toFilterIndex = toGroupIndex === editingGroupIndex ? editingFilterIndex : toGroup ? toGroup.get('filters').count() : 0;
    const nextFiltering = moveFilter(updateFilter(filtering, editingGroupIndex, editingFilterIndex, draftFilter), editingGroupIndex, editingFilterIndex, toGroupIndex, toFilterIndex);
    setFiltering(nextFiltering);
  };
  const handleStagedFilterChange = (draftFilter, draftGroupIndex) => {
    const {
      editingStagedFilterIndex
    } = editingState || {};
    clearEditingState();
    const toGroupIndex = draftGroupIndex === undefined ? groupCount : draftGroupIndex;
    const toGroup = groups.get(toGroupIndex);
    const toFilterIndex = toGroup ? toGroup.get('filters').count() : 0;
    const nextFiltering = removeStagedFilter(addFilter(filtering, draftFilter, toGroupIndex, toFilterIndex), editingStagedFilterIndex);
    setFiltering(nextFiltering);
  };
  const handleRemoveStagedFilter = index => {
    clearEditingState();
    setFiltering(removeStagedFilter(filtering, index));
  };
  const handleStagedFilterEdit = index => {
    setEditingState(EditingState({
      editingStagedFilterIndex: index
    }));
  };
  const handleCloneFilterToGroup = (draftFilter, groupId) => {
    const nextFiltering = addFilter(filtering, draftFilter, groupId);
    setFiltering(nextFiltering);
  };
  return {
    handleFilterEdit,
    handleCancelEdit,
    handleFilterRemove,
    handleFilterChange,
    handleStagedFilterChange,
    handleGroupConditionChange,
    handleRemoveStagedFilter,
    handleStagedFilterEdit,
    handleCloneFilterToGroup
  };
};
const FilterEditContext = /*#__PURE__*/createContext();
export const FilterEditProvider = ({
  sourceMeta,
  onChange,
  children
}) => {
  const [editingState, setEditingState] = useState(EditingState({}));
  const callbacks = useEditCallbacks(sourceMeta, onChange, editingState, setEditingState);
  return /*#__PURE__*/_jsx(FilterEditContext.Provider, {
    value: [editingState, callbacks],
    children: children
  });
};
export const useFilterEdit = () => {
  return useContext(FilterEditContext);
};